import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "userdata:portal";
const WARNING_MS = 5 * 60 * 1000; // 5 min antes del exp

function decodeJwtExp(token) {
  try {
    const payload = token.split(".")[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return decoded?.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

function readToken() {
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return data?.token || null;
  } catch {
    return null;
  }
}

/**
 * Vigila el `exp` del JWT del portal y dispara warning antes de expirar
 * + logout automático al expirar.
 *
 * Devuelve { warning, dismiss, logout, msUntilExpiry }.
 */
export function useSessionExpiry({ onLogout }) {
  const [warning, setWarning] = useState(false);
  const [expiryAt, setExpiryAt] = useState(null);

  useEffect(() => {
    const token = readToken();
    if (!token) return;
    const exp = decodeJwtExp(token);
    if (!exp) return;
    setExpiryAt(exp);

    const now = Date.now();
    const msToExpiry = exp - now;
    const msToWarning = msToExpiry - WARNING_MS;

    let warnTimer = null;
    let logoutTimer = null;

    if (msToWarning > 0) {
      warnTimer = setTimeout(() => setWarning(true), msToWarning);
    } else if (msToExpiry > 0) {
      // Ya estamos en la ventana de warning.
      setWarning(true);
    }

    if (msToExpiry > 0) {
      logoutTimer = setTimeout(() => {
        if (onLogout) onLogout();
      }, msToExpiry);
    } else {
      // Ya expiró: logout inmediato.
      if (onLogout) onLogout();
    }

    return () => {
      if (warnTimer) clearTimeout(warnTimer);
      if (logoutTimer) clearTimeout(logoutTimer);
    };
  }, [onLogout]);

  const dismiss = useCallback(() => setWarning(false), []);
  const msUntilExpiry = expiryAt ? expiryAt - Date.now() : null;

  return { warning, dismiss, msUntilExpiry, expiryAt };
}
